import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Tab,
  Tabs,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUpload, faImage } from '@fortawesome/free-solid-svg-icons';
import CustomSelect from '@src/components/CustomSelect';
import CustomMultiSelect from '@src/components/CustomMultiSelect';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import {
  TOOL_TYPES,
  AI_AGENTS,
  MAX_FILE_SIZE_MB,
  ALLOWED_IMAGE_TYPES,
} from '@src/constants';
import { StyledCreateToolModal } from './index.style';

const CreateToolModal = ({ open, onClose, onSuccess, appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [availableModels, setAvailableModels] = useState([]);

  const [formData, setFormData] = useState({
    code: '',
    type: TOOL_TYPES.AI_TOOL,
    categories: [],
    prompt: '',
    agent: '',
    model: '',
    logoFile: null,
    logoPreview: null,
    iconFile: null,
    iconPreview: null,
    vi: {
      name: '',
      title: '',
      description: '',
      welcomeMessage: '',
      sampleSuggestion1: '',
      sampleSuggestion2: '',
      sampleSuggestion3: '',
    },
    en: {
      name: '',
      title: '',
      description: '',
      welcomeMessage: '',
      sampleSuggestion1: '',
      sampleSuggestion2: '',
      sampleSuggestion3: '',
    },
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedAgent) {
      const agent = AI_AGENTS.find((a) => a.id === selectedAgent);
      setAvailableModels(agent?.models || []);
      setFormData((prev) => ({ ...prev, agent: selectedAgent, model: '' }));
    } else {
      setAvailableModels([]);
    }
  }, [selectedAgent]);

  const fetchCategories = async () => {
    try {
      const response = await apis.category.getCategories();
      if (response?.status) {
        setCategories(response.result?.data || []);
      }
    } catch (error) {
      enqueueSnackbar(t('fetchCategoriesError'), { variant: 'error' });
    }
  };

  const handleInputChange = (field, value, lang = null) => {
    if (lang) {
      setFormData((prev) => ({
        ...prev,
        [lang]: {
          ...prev[lang],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field] || (lang && errors[lang]?.[field])) {
      const newErrors = { ...errors };
      if (lang) {
        if (newErrors[lang]) {
          delete newErrors[lang][field];
        }
      } else {
        delete newErrors[field];
      }
      setErrors(newErrors);
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      enqueueSnackbar(t('invalidFileType'), { variant: 'error' });
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      enqueueSnackbar(t('fileSizeExceeded', { size: MAX_FILE_SIZE_MB }), {
        variant: 'error',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (fileType === 'logo') {
        setFormData((prev) => ({
          ...prev,
          logoFile: file,
          logoPreview: event.target.result,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          iconFile: file,
          iconPreview: event.target.result,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) newErrors.code = t('toolCodeRequired');
    if (!formData.logoFile) newErrors.logo = t('logoRequired');
    if (!formData.iconFile) newErrors.icon = t('iconRequired');
    if (!formData.prompt.trim()) newErrors.prompt = t('promptRequired');
    if (formData.categories.length === 0)
      newErrors.categories = t('categoriesRequired');

    if (
      (formData.type === TOOL_TYPES.AI_CHAT ||
        formData.type === TOOL_TYPES.AI_MODEL) &&
      !formData.agent
    ) {
      newErrors.agent = t('agentRequired');
    }
    if (
      (formData.type === TOOL_TYPES.AI_CHAT ||
        formData.type === TOOL_TYPES.AI_MODEL) &&
      !formData.model
    ) {
      newErrors.model = t('modelRequired');
    }

    const viErrors = {};
    if (!formData.vi.name.trim()) viErrors.name = t('nameRequired');
    if (!formData.vi.title.trim()) viErrors.title = t('titleRequired');
    if (!formData.vi.description.trim())
      viErrors.description = t('descriptionRequired');
    if (!formData.vi.welcomeMessage.trim())
      viErrors.welcomeMessage = t('welcomeMessageRequired');

    if (Object.keys(viErrors).length > 0) {
      newErrors.vi = viErrors;
    }

    const enErrors = {};
    if (!formData.en.name.trim()) enErrors.name = t('nameRequired');
    if (!formData.en.title.trim()) enErrors.title = t('titleRequired');
    if (!formData.en.description.trim())
      enErrors.description = t('descriptionRequired');
    if (!formData.en.welcomeMessage.trim())
      enErrors.welcomeMessage = t('welcomeMessageRequired');

    if (Object.keys(enErrors).length > 0) {
      newErrors.en = enErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareFormData = () => {
    const data = new FormData();

    data.append('code', formData.code.trim());
    data.append('type', formData.type);
    data.append('prompt', formData.prompt.trim());
    if (appId) data.append('appId', appId);

    if (formData.agent) data.append('agent', formData.agent);
    if (formData.model) data.append('model', formData.model);

    if (formData.logoFile) data.append('logo', formData.logoFile);
    if (formData.iconFile) data.append('icon', formData.iconFile);

    formData.categories.forEach((categoryId, index) => {
      data.append(`categories[${index}]`, categoryId);
    });

    Object.entries(formData.vi).forEach(([key, value]) => {
      if (value.trim()) {
        data.append(`translations[vi][${key}]`, value.trim());
      }
    });

    Object.entries(formData.en).forEach(([key, value]) => {
      if (value.trim()) {
        data.append(`translations[en][${key}]`, value.trim());
      }
    });

    return data;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const data = prepareFormData();
      const response = await apis.toolManagement.createTool(data);

      if (response?.status) {
        enqueueSnackbar(t('createToolSuccess'), { variant: 'success' });
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('createToolError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      code: '',
      type: TOOL_TYPES.AI_TOOL,
      categories: [],
      prompt: '',
      agent: '',
      model: '',
      logoFile: null,
      logoPreview: null,
      iconFile: null,
      iconPreview: null,
      vi: {
        name: '',
        title: '',
        description: '',
        welcomeMessage: '',
        sampleSuggestion1: '',
        sampleSuggestion2: '',
        sampleSuggestion3: '',
      },
      en: {
        name: '',
        title: '',
        description: '',
        welcomeMessage: '',
        sampleSuggestion1: '',
        sampleSuggestion2: '',
        sampleSuggestion3: '',
      },
    });
    setErrors({});
    setActiveTab(0);
    setSelectedAgent('');
    onClose();
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const agentOptions = AI_AGENTS.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const modelOptions = availableModels.map((model) => ({
    value: model.id,
    label: model.name,
  }));

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open]);

  const renderLanguageForm = (lang) => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography className="label-input" variant="h6">
          {t('toolName')} <span>*</span>
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={formData[lang].name}
          onChange={(e) => handleInputChange('name', e.target.value, lang)}
          error={!!errors[lang]?.name}
          helperText={errors[lang]?.name}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography className="label-input" variant="h6">
          {t('toolTitle')} <span>*</span>
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={formData[lang].title}
          onChange={(e) => handleInputChange('title', e.target.value, lang)}
          error={!!errors[lang]?.title}
          helperText={errors[lang]?.title}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography className="label-input" variant="h6">
          {t('description')} <span>*</span>
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={3}
          value={formData[lang].description}
          onChange={(e) =>
            handleInputChange('description', e.target.value, lang)
          }
          error={!!errors[lang]?.description}
          helperText={errors[lang]?.description}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography className="label-input" variant="h6">
          {t('welcomeMessage')} <span>*</span>
        </Typography>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={2}
          value={formData[lang].welcomeMessage}
          onChange={(e) =>
            handleInputChange('welcomeMessage', e.target.value, lang)
          }
          error={!!errors[lang]?.welcomeMessage}
          helperText={errors[lang]?.welcomeMessage}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 1
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={formData[lang].sampleSuggestion1}
          onChange={(e) =>
            handleInputChange('sampleSuggestion1', e.target.value, lang)
          }
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 2
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={formData[lang].sampleSuggestion2}
          onChange={(e) =>
            handleInputChange('sampleSuggestion2', e.target.value, lang)
          }
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 3
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={formData[lang].sampleSuggestion3}
          onChange={(e) =>
            handleInputChange('sampleSuggestion3', e.target.value, lang)
          }
        />
      </Grid>
    </Grid>
  );

  return (
    <StyledCreateToolModal open={open} onClose={handleClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('createTool')}
        <IconButton onClick={handleClose}>
          <FontAwesomeIcon
            icon={faXmark}
            color={COLOR.text.highlight}
            size="sm"
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Box className="form-section">
          <Typography className="section-title">
            {t('basicInformation')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('toolCode')} <span>*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                error={!!errors.code}
                helperText={errors.code}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('toolType')} <span>*</span>
              </Typography>
              <RadioGroup
                row
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <FormControlLabel
                  value={TOOL_TYPES.AI_TOOL}
                  control={<Radio />}
                  label="AI Tool"
                />
                <FormControlLabel
                  value={TOOL_TYPES.AI_CHAT}
                  control={<Radio />}
                  label="AI Chat"
                />
                <FormControlLabel
                  value={TOOL_TYPES.AI_MODEL}
                  control={<Radio />}
                  label="AI Model"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography className="label-input" variant="h6">
                {t('categories')} <span>*</span>
              </Typography>
              <CustomMultiSelect
                options={categoryOptions}
                value={formData.categories}
                onChange={(value) => handleInputChange('categories', value)}
                error={!!errors.categories}
                helperText={errors.categories}
                placeholder={t('selectCategories')}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className="label-input" variant="h6">
                {t('prompt')} <span>*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={4}
                value={formData.prompt}
                onChange={(e) => handleInputChange('prompt', e.target.value)}
                error={!!errors.prompt}
                helperText={errors.prompt}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography className="section-title">
            {t('aiConfiguration')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('agent')} <span>*</span>
              </Typography>
              <CustomSelect
                options={agentOptions}
                value={selectedAgent}
                onChange={setSelectedAgent}
                error={!!errors.agent}
                helperText={errors.agent}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('model')} <span>*</span>
              </Typography>
              <CustomSelect
                options={modelOptions}
                value={formData.model}
                onChange={(value) => handleInputChange('model', value)}
                disabled={!selectedAgent}
                error={!!errors.model}
                helperText={errors.model}
              />
            </Grid>
          </Grid>
        </Box>

        {/* File Upload */}
        <Box className="form-section">
          <Typography className="section-title">{t('mediaFiles')}</Typography>
          <Box className="file-upload-section">
            <Box className="file-upload-item">
              <Typography className="label-input" variant="h6">
                {t('logo')} <span>*</span>
              </Typography>
              <Box
                className="file-preview"
                onClick={() => document.getElementById('logo-upload').click()}
              >
                {formData.logoPreview ? (
                  <img src={formData.logoPreview} alt="Logo preview" />
                ) : (
                  <FontAwesomeIcon icon={faUpload} color={COLOR.blueGray[64]} />
                )}
              </Box>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e, 'logo')}
              />
              {errors.logo && (
                <Typography className="error-text">{errors.logo}</Typography>
              )}
            </Box>
            <Box className="file-upload-item">
              <Typography className="label-input" variant="h6">
                {t('icon')} <span>*</span>
              </Typography>
              <Box
                className="file-preview"
                onClick={() => document.getElementById('icon-upload').click()}
              >
                {formData.iconPreview ? (
                  <img src={formData.iconPreview} alt="Icon preview" />
                ) : (
                  <FontAwesomeIcon icon={faImage} color={COLOR.blueGray[64]} />
                )}
              </Box>
              <input
                id="icon-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e, 'icon')}
              />
              {errors.icon && (
                <Typography className="error-text">{errors.icon}</Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box className="form-section">
          <Typography className="section-title">{t('content')}</Typography>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            className="language-tabs"
          >
            <Tab label={t('vietnamese')} />
            <Tab label={t('english')} />
          </Tabs>
          <Box mt={2}>
            {activeTab === 0 && renderLanguageForm('vi')}
            {activeTab === 1 && renderLanguageForm('en')}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="text">
          {t('cancel')}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? t('creating') : t('create')}
        </Button>
      </DialogActions>
    </StyledCreateToolModal>
  );
};

export default CreateToolModal;
